import type { Conversation } from './Conversation';
import { isAssistantMessage, isToolMessage } from './types';

export interface SubagentConversationEntry {
    conversation: Conversation;
    /** Name of the tool call that spawned it, e.g. `find_templates`. */
    toolName: string;
    /** Nesting depth below the conversation the walk started from; direct children are 0. */
    depth: number;
}

/**
 * Flattens a conversation's subagent tree into transcript order: each child in
 * the order its spawning tool call appears in the parent, immediately followed
 * by that child's own subtree.
 *
 * Used to index every subagent of a conversation in one place, so a saved run
 * can be navigated without expanding each tool step to find the nested
 * conversation buried in its result.
 */
export function flattenSubagentConversations(
    conversation: Conversation,
    depth = 0,
): SubagentConversationEntry[] {
    const { messages } = conversation.getState();

    const toolNamesByToolCallId = new Map<string, string>();
    messages.forEach((message) => {
        if (isAssistantMessage(message) && message.tool_calls) {
            message.tool_calls.forEach((toolCall) => {
                toolNamesByToolCallId.set(toolCall.id, toolCall.function.name);
            });
        }
    });

    const entries: SubagentConversationEntry[] = [];
    messages.forEach((message) => {
        if (!isToolMessage(message)) {
            return;
        }
        conversation.getChildConversationsForToolCall(message.tool_call_id).forEach((child) => {
            entries.push({
                conversation: child,
                toolName: toolNamesByToolCallId.get(message.tool_call_id) ?? 'unknown tool',
                depth,
            });
            entries.push(...flattenSubagentConversations(child, depth + 1));
        });
    });

    return entries;
}

/** Path of the staff-only detail page for a conversation. */
export function conversationDetailUrl(conversationId: string): string {
    return `/conversations/${conversationId}/`;
}
